
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

export function SubscriptionChart() {
  const { data: chartData, isLoading } = useQuery({
    queryKey: ['subscription-breakdown'],
    queryFn: async () => {
      const { data: subscribers } = await supabase
        .from('subscribers')
        .select('subscription_tier, subscribed, is_trial, is_admin_managed');

      if (!subscribers) return [];

      const breakdown = subscribers.reduce((acc: any, sub) => {
        let category;
        if (sub.is_admin_managed) {
          category = 'Admin Managed';
        } else if (sub.is_trial) {
          category = 'Trial';
        } else if (sub.subscribed) {
          category = sub.subscription_tier || 'Premium';
        } else {
          category = 'Free';
        }

        acc[category] = (acc[category] || 0) + 1;
        return acc;
      }, {});

      return Object.entries(breakdown).map(([name, value]) => ({
        name,
        value,
        percentage: ((value as number / subscribers.length) * 100).toFixed(1)
      }));
    }
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Subscription Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center">
            <div className="animate-pulse text-gray-500">Loading chart...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Subscription Breakdown</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percentage }) => `${name}: ${percentage}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {chartData?.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
